import PbEnhancedElement from "../pb_enhanced_element";
import {
  isNativeSelectMenuInteraction,
  targetIsInsidePortaledFloatingKit,
} from "../utilities/floatingPortalHosts";

const DIALOG_WRAPPER_SELECTOR = "[data-pb-dialog-wrapper]";

export default class PbDialog extends PbEnhancedElement {
  static get selector() {
    return DIALOG_WRAPPER_SELECTOR;
  }

  connect() {
    // Store references to this instance's specific elements
    this.dialogElement = this.element.querySelector(".pb_dialog_rails")
    this.dialogId = this.dialogElement?.id
    this.managedTriggers = new Set()
    
    this.domContentLoadedHandler = () => this.setupDialog()
    this.turboFrameLoadHandler = () => this.setupDialog()
    
    // If DOM is already loaded, setup immediately; otherwise wait for DOMContentLoaded
    if (document.readyState === "loading") {
      window.addEventListener("DOMContentLoaded", this.domContentLoadedHandler)
    } else {
      this.setupDialog()
    }
    window.addEventListener("turbo:frame-load", this.turboFrameLoadHandler)

    // Code for custom_event_type setup (can take multiple events in a string separated by commas)
    const customEventTypeString = this.element.dataset.customEventType
    if (customEventTypeString && !this.element.hasAttribute("data-custom-event-handled")) {
      this.customEventTypes = customEventTypeString.split(",").map(e => e.trim()).filter(Boolean)
      this.customEventTypes.forEach(eventType => {
        window.addEventListener(eventType, this.handleCustomEvent)
      })

      this.element.setAttribute("data-custom-event-handled", "true")
    }
  }

  disconnect() {
    // Clean up window event listeners
    if (this.domContentLoadedHandler) {
      window.removeEventListener("DOMContentLoaded", this.domContentLoadedHandler)
    }
    if (this.turboFrameLoadHandler) {
      window.removeEventListener("turbo:frame-load", this.turboFrameLoadHandler)
    }

    // Clean up custom event listeners
    if (this.customEventTypes && Array.isArray(this.customEventTypes)) {
      this.customEventTypes.forEach(eventType => {
        window.removeEventListener(eventType, this.handleCustomEvent)
      })
    }

    // Clean up only the triggers that this instance managed
    this.managedTriggers.forEach((trigger) => {
      if (trigger._openDialogClickHandler) {
        trigger.removeEventListener("click", trigger._openDialogClickHandler)
        delete trigger._openDialogClickHandler
      }
      if (trigger._closeDialogClickHandler) {
        trigger.removeEventListener("click", trigger._closeDialogClickHandler)
        delete trigger._closeDialogClickHandler
      }
    })

    // Clean up this dialog's outside click handler
    if (this.dialogElement && this.dialogElement._outsideClickHandler) {
      this.dialogElement.removeEventListener("mousedown", this.dialogElement._outsideClickHandler)
      delete this.dialogElement._outsideClickHandler
    }
  }

  handleCustomEvent = (event) => {
    const dialogId = event.detail?.dialogId || this.element.querySelector("dialog")?.id
    const dialog = dialogId && document.getElementById(dialogId)
  
    if (!dialog) {
      console.warn(`[PbDialog] Could not find dialog with ID '${dialogId}'`)
      return
    }
  
    this.setupDialog()
  
    const action = event.detail?.action || 'open'
  
    // Known Actions - four "standard" dialog actions that felt like things devs might want to do
    const knownActions = ['open', 'close', 'clickConfirm', 'clickCancel'];
  
    if (knownActions.includes(action)) {
      switch (action) {
        case 'open':
          if (!dialog.open) dialog.showModal()
          break
        case 'close':
          if (dialog.open) dialog.close(event.detail?.returnValue)
          break
        case 'clickConfirm':
          this.triggerButtonClick(dialog, event, 'confirm')
          break
        case 'clickCancel':
          this.triggerButtonClick(dialog, event, 'cancel')
          break
      }
    } 
    // Custom Actions - flexible for Turbo or other integration
    else if (typeof event.detail?.customAction === 'function') {
      event.detail.customAction({ dialog, event })
    } else if (window.pbDialogActions?.[action]) {
      // Can register custom actions globally
      window.pbDialogActions[action]({ dialog, event })
    } else {
      console.warn(`[PbDialog] Unknown action: ${action}`)
    }
  }

  triggerButtonClick(dialog, event, type) {
    const buttonId = event.detail?.[`${type}ButtonId`] || 
                     dialog.closest('[data-pb-dialog-wrapper]')?.dataset[`${type}ButtonId`]
    const button = buttonId ? document.getElementById(buttonId) : 
                  dialog.querySelector(`[data-${type}-button]`)
  
    if (button) {
      button.click()
    } else {
      console.warn(`[PbDialog] Could not find ${type} button for dialog`)
    }
  }

  setupDialog() {
    // Only set up triggers and dialogs that belong to this instance
    if (!this.dialogId) return
    
    const openTrigger = document.querySelectorAll(`[data-open-dialog="${this.dialogId}"]`);
    const closeTrigger = document.querySelectorAll(`[data-close-dialog="${this.dialogId}"]`);
    const dialogs = this.dialogElement ? [this.dialogElement] : []

    const loadingButton = document.querySelector('[data-disable-with="Loading"]');
    if (loadingButton && !loadingButton.dataset.listenerAttached) {
      loadingButton.addEventListener("click", function () {
        const okayLoadingButton = document.querySelector('[data-disable-with="Loading"]');
        const cancelButton = document.querySelector('[data-disable-cancel-with="Loading"]');
        let currentClass = okayLoadingButton.className;
        let cancelClass = cancelButton ? cancelButton.className : "";

        let newClass = currentClass.replace("_enabled", "_disabled_loading");
        let newCancelClass = cancelClass.replace("_enabled", "_disabled");

        // Disable the buttons
        okayLoadingButton.disabled = true;
        if (cancelButton) cancelButton.disabled = true;

        okayLoadingButton.className = newClass;
        if (cancelButton) cancelButton.className = newCancelClass;
      });
    
      // Prevent multiple registrations
      loadingButton.dataset.listenerAttached = "true";
    }
    
    openTrigger.forEach((open) => {
      const originalClickHandler = open._openDialogClickHandler
      if (originalClickHandler) open.removeEventListener("click", originalClickHandler)

      open._openDialogClickHandler = () => {
        const openTriggerData = open.dataset.openDialog;
        const targetDialogOpen = document.getElementById(openTriggerData)
        if (targetDialogOpen && !targetDialogOpen.open) targetDialogOpen.showModal()
      };

      open.addEventListener("click", open._openDialogClickHandler)
      this.managedTriggers.add(open)
    });

    closeTrigger.forEach((close) => {
      const originalClickHandler = close._closeDialogClickHandler
      if (originalClickHandler) close.removeEventListener("click", originalClickHandler)

      close._closeDialogClickHandler = () => {
        const closeTriggerData = close.dataset.closeDialog;
        const targetDialogClose = document.getElementById(closeTriggerData)
        if (targetDialogClose) targetDialogClose.close();
      };

      close.addEventListener("click", close._closeDialogClickHandler)
      this.managedTriggers.add(close)
    });

    // Close dialog box on outside click
    dialogs.forEach((dialogElement) => {
      const originalMousedownHandler = dialogElement._outsideClickHandler
      if (originalMousedownHandler) dialogElement.removeEventListener("mousedown", originalMousedownHandler)
      dialogElement._outsideClickHandler = (event) => {
        const dialogParentDataset = dialogElement.parentElement.dataset
        if (dialogParentDataset.overlayClick === "overlay_close") return

        const target = event.target
        if (targetIsInsidePortaledFloatingKit(target, dialogElement.id)) return
        if (isNativeSelectMenuInteraction(dialogElement, target)) return

        const dialogContent =
          dialogElement.querySelector(".pb_dialog_scroll_region") || dialogElement
        const dialogModal = dialogContent.getBoundingClientRect()
        const clickedOutsideDialogModal = event.clientX < dialogModal.left ||
          event.clientX > dialogModal.right ||
          event.clientY < dialogModal.top ||
          event.clientY > dialogModal.bottom

        if (clickedOutsideDialogModal) {
          dialogElement.close()
          event.stopPropagation()
        }
      }

      dialogElement.addEventListener("mousedown", dialogElement._outsideClickHandler);
    })
  }
}
